import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

function unwrapPriceApiPayload(raw) {
  let data = raw
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return raw
    }
  }
  if (data && data.body != null) {
    const body = data.body
    if (typeof body === 'string') {
      try {
        return JSON.parse(body)
      } catch {
        return body
      }
    }
    return body
  }
  return data
}

const PriceTrackerView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [price, setPrice] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchPrice()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const formatDateToIST = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(date)
  }

  /** Date only: DD/MM/YYYY (IST). */
  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date)
  }

  const fetchPrice = async () => {
    if (!id) {
      toast.error('Price ID is missing', { autoClose: 2000 })
      navigate('/priceTracker')
      return
    }

    setIsLoading(true)
    try {
      const payload = {
        action: 'get-price-by-id',
        id,
        includeDownloadUrls: true
      }

      const response = await axios.post(API_URL, payload)
      const responseData = unwrapPriceApiPayload(response.data)
      const pricePayload =
        responseData?.success === true
          ? responseData.price || responseData.data?.price
          : null

      if (pricePayload && typeof pricePayload === 'object') {
        setPrice(pricePayload)
      } else {
        toast.error(responseData?.message || 'Failed to fetch price details', {
          autoClose: 2000
        })
        navigate('/priceTracker')
      }
    } catch (error) {
      console.error('Fetch price error:', error)
      toast.error('Failed to load price details', { autoClose: 2000 })
      navigate('/priceTracker')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Price</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/priceTracker">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!price) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Price</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/priceTracker">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>Price not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const currentUserId = localStorage.getItem('id') || ''
  const currentRole = (localStorage.getItem('role') || '').toLowerCase()
  const priceCreatedById = price?.createdBy?._id || price?.createdBy || ''
  const canEdit =
    currentRole === 'admin' ||
    (currentUserId && priceCreatedById && currentUserId === priceCreatedById)

  const downloadFiles = Array.isArray(price.downloadFiles) ? price.downloadFiles : []

  const productImageS3Key =
    price.productImage &&
    typeof price.productImage === 'object' &&
    price.productImage.s3Key
      ? String(price.productImage.s3Key)
      : ''

  const isProductImageDownloadFile = (f) => {
    if (!f || typeof f !== 'object') return false
    if (f.kind === 'productImage') return true
    const key = f.s3Key != null ? String(f.s3Key) : ''
    if (key.startsWith('Product_attachment/')) return true
    if (productImageS3Key && key === productImageS3Key) return true
    return false
  }

  const productImageDownload = downloadFiles.find((f) => isProductImageDownloadFile(f))
  const attachmentDownloadFiles = downloadFiles.filter((f) => !isProductImageDownloadFile(f))

  const formatMoney = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '-'
    return Number(value).toLocaleString('en-IN')
  }

  const displayUom = (value) => {
    if (value == null) return '-'
    const s = String(value).trim()
    return s === '' ? '-' : s
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">View Price</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/priceTracker">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal">
                    <div className="card-body">
                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Supplier Name
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">
                            {Array.isArray(price.supplierName)
                              ? price.supplierName.filter(Boolean).join(', ')
                              : price.supplierName || '-'}
                          </div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Product Name
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{price.productName || '-'}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Purchase UOM
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">
                            {displayUom(price.purchaseUom ?? price.uom)}
                          </div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Purchase price per unit
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">
                            {formatMoney(
                              price.purchasePricePerUnit != null
                                ? price.purchasePricePerUnit
                                : price.pricePerUnit
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Sales UOM
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{displayUom(price.salesUom)}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Sales price per unit
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">
                            {formatMoney(price.salesPricePerUnit)}
                          </div>
                        </div>
                      </div>

                      <div className="form-group row align-items-start">
                        <label className="col-12 col-md-4 col-form-label">
                          Product image
                        </label>
                        <div className="col-12 col-md-8">
                          {price.productImage &&
                          typeof price.productImage === 'object' &&
                          price.productImage.s3Key ? (
                            <div className="txtdsply">
                              {productImageDownload?.downloadUrl ? (
                                String(
                                  productImageDownload.contentType ||
                                    price.productImage.contentType ||
                                    ''
                                )
                                  .toLowerCase()
                                  .startsWith('image/') ? (
                                  <div
                                    className="price-product-image-container"
                                    style={{
                                      width: '100%',
                                      maxWidth: '320px',
                                      height: '200px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      overflow: 'hidden',
                                      border: '1px solid #dee2e6',
                                      borderRadius: '6px',
                                      backgroundColor: '#f8f9fa',
                                      boxSizing: 'border-box'
                                    }}
                                  >
                                    <a
                                      href={productImageDownload.downloadUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      title="Open image"
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%',
                                        height: '100%',
                                        padding: '8px',
                                        boxSizing: 'border-box'
                                      }}
                                    >
                                      <img
                                        src={productImageDownload.downloadUrl}
                                        alt=""
                                        style={{
                                          maxWidth: '100%',
                                          maxHeight: '100%',
                                          width: 'auto',
                                          height: 'auto',
                                          objectFit: 'contain',
                                          cursor: 'pointer',
                                          verticalAlign: 'middle'
                                        }}
                                      />
                                    </a>
                                  </div>
                                ) : (
                                  <span className="text-muted" style={{ fontSize: '13px' }}>
                                    Preview not available for this file type.
                                  </span>
                                )
                              ) : (
                                <span className="text-muted" style={{ fontSize: '13px' }}>
                                  Preview link not included in this response.
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="txtdsply">-</div>
                          )}
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Valid From
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{formatDate(price.priceValidFrom)}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Valid Till
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{formatDate(price.priceValidTill)}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Remarks</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply" style={{ whiteSpace: 'pre-wrap' }}>
                            {price.remarks != null && String(price.remarks).trim() !== ''
                              ? String(price.remarks).trim()
                              : '-'}
                          </div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Status
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">
                            <span
                              className={`badge ${
                                price.status === 'active'
                                  ? 'badge-success'
                                  : 'badge-secondary'
                              }`}
                              style={{ fontSize: '90%', fontWeight: 550 }}
                            >
                              {price.status || '-'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Attachments
                        </label>
                        <div className="col-12 col-md-8">
                          {attachmentDownloadFiles.length === 0 ? (
                            <div className="txtdsply">-</div>
                          ) : (
                            <ul className="mb-0 pl-3" style={{ fontSize: '13px' }}>
                              {attachmentDownloadFiles.map((f, idx) => (
                                <li key={`${f.s3Key}-${idx}`}>
                                  <a href={f.downloadUrl} target="_blank" rel="noreferrer">
                                    {f.originalFileName || f.s3Key}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Created On
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{formatDateToIST(price.createdAt)}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">
                          Updated On
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{formatDateToIST(price.updatedAt)}</div>
                        </div>
                      </div>
                    </div>

                    <div className="card-footer text-center">
                      {canEdit && (
                        <Link to={`/priceTrackerEdit/${price.id}`} className="btn btn-blue mr-2">
                          Edit
                        </Link>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PriceTrackerView